import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  assertValueSetupFieldValue,
  isNotRelevantField,
  type NonConfigurationSimpleValueField,
  type ValidationFieldValue,
  type ValueSetupFieldValue,
} from '../../types/templateModel'
import { generateEmptyFieldValue } from '../../uiModels/createEmptyFieldHelpers'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { FieldValidationsValueDetails } from './FieldValidationsValueDetails'
import { useFieldValidations } from './useFieldValidations'

type Props = {
  field: NonConfigurationSimpleValueField
  errors?: Optional<
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
}

export const TemplateFieldValidations = ({ field, errors }: Props) => {
  const validations = useFieldValidations(field)
  const translate = useTranslate()
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const handleValidationChanged = (
    nextValue: Optional<ValidationFieldValue>,
    index: number,
  ) => {
    assertValueSetupFieldValue(field.value)

    const nextValidations = [...(validations ?? [])]

    if (nextValue) {
      nextValidations.splice(index, 1, nextValue)
    } else {
      nextValidations.splice(index, 1)
    }

    const { validations: oldValidations, ...otherFields } = field.value

    return (field.update as (v: ValueSetupFieldValue) => Promise<void>)({
      ...otherFields,
      ...(nextValidations.length ? { validations: nextValidations } : {}),
    })
  }

  const handleAddValidation = () => {
    const fieldValue = (field.value ??
      generateEmptyFieldValue({
        field,
        meta: metaModel,
      })) as ValueSetupFieldValue
    const nextValidations = [
      ...(fieldValue?.validations ?? []),
      {} as ValidationFieldValue,
    ]

    return (field.update as (v: ValueSetupFieldValue) => Promise<void>)({
      ...fieldValue,
      validations: nextValidations,
      origin: fieldValue.path,
    })
  }

  if (!validations?.length && !isDraftTemplate) {
    return <span>-</span>
  }

  return (
    <>
      {isDraftTemplate && (
        <Button
          className="mb-2 whitespace-nowrap"
          onClick={handleAddValidation}
          disabled={isNotRelevantField(field)}
          startIcon="PlusCircle"
        >
          {translate(
            'templateViewer.fieldDetailsModal.validations.addValidation',
          )}
        </Button>
      )}
      <FieldValidationsValueDetails
        errors={errors}
        validations={validations ?? []}
        onValidationChange={handleValidationChanged}
        fieldPath={field.path}
      />
    </>
  )
}
